import Swiper, { Scrollbar } from "swiper";

export default () => ({
  swiper: null,
  currentSlide: 0,
  totalSlides: 0,
  zoomLevel: 1,
  isZoomed: false,
  initialDistance: 0,
  initialScale: 1,

  initSlider() {
    this.swiper = new Swiper(this.$refs.slider, {
      slidesPerView: 1,
      allowTouchMove: true,
      scrollbar: {
        el: ".swiper-scrollbar",
      },
      breakpoints: {
        1024: {
          slidesPerView: 0,
          spaceBetween: 0,
          slidesOffsetBefore: 0,
        },
      },
      modules: [Scrollbar],
      on: {
        init: () => {
          this.totalSlides = this.swiper.slides.length;
          
          // Force scrollbar to recalculate its size
          if (this.swiper.scrollbar) {
            this.swiper.scrollbar.updateSize();
            this.swiper.scrollbar.setTranslate(0);
          }

          // Add zoom functionality to each slide
          this.$nextTick(() => {
            this.initZoomOnSlides();
          });
        },
        slideChange: () => {
          this.currentSlide = this.swiper.realIndex + 1;
          // Reset zoom when changing slides
          this.resetZoom();
        }
      }
    });
  },

  initZoomOnSlides() {
    const slides = this.$refs.slider.querySelectorAll('.swiper-slide');
    
    slides.forEach((slide, index) => {
      const image = slide.querySelector('img');
      if (!image) return;

      // Find the clickable container (modal-opener or div with click handler)
      const clickableContainer = slide.querySelector('[x-data]') || slide.querySelector('.product__modal-opener');
      if (!clickableContainer) return;

      // Create zoom container
      const zoomContainer = document.createElement('div');
      zoomContainer.className = 'zoom-container relative w-full h-full overflow-hidden';
      zoomContainer.style.transformOrigin = 'center center';
      
      // Insert zoom container inside the clickable container
      const imageWrapper = image.closest('.relative') || image.parentNode;
      imageWrapper.insertBefore(zoomContainer, image);
      zoomContainer.appendChild(image);
      
      // Add zoom functionality
      this.addZoomHandlers(zoomContainer, image, index, clickableContainer);
    });
  },

  addZoomHandlers(container, image, slideIndex, clickableContainer) {
    let currentScale = 1;
    let startDistance = 0;
    let startScale = 1;
    let lastTapTime = 0;
    let isZooming = false;
    let isPanning = false;
    let startX = 0;
    let startY = 0;
    let translateX = 0;
    let translateY = 0;

    // Pinch to zoom
    container.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        e.stopPropagation();
        isZooming = true;
        startDistance = this.getDistance(e.touches[0], e.touches[1]);
        startScale = currentScale;
      } else if (e.touches.length === 1 && currentScale > 1) {
        isPanning = true;
        startX = e.touches[0].clientX - translateX;
        startY = e.touches[0].clientY - translateY;
      }
    }, { passive: false });

    container.addEventListener('touchmove', (e) => {
      if (e.touches.length === 2 && isZooming) {
        e.preventDefault();
        e.stopPropagation();
        const currentDistance = this.getDistance(e.touches[0], e.touches[1]);
        const scale = (currentDistance / startDistance) * startScale;
        
        // Limit zoom between 1x and 3x
        const newScale = Math.min(Math.max(scale, 1), 3);
        currentScale = newScale;
        
        this.applyZoom(container, newScale, translateX, translateY);
      } else if (e.touches.length === 1 && isPanning && currentScale > 1) {
        e.preventDefault();
        e.stopPropagation();
        translateX = e.touches[0].clientX - startX;
        translateY = e.touches[0].clientY - startY;
        
        // Limit panning to keep image visible
        const maxTranslateX = (currentScale - 1) * container.offsetWidth / 2;
        const maxTranslateY = (currentScale - 1) * container.offsetHeight / 2;
        
        translateX = Math.min(Math.max(translateX, -maxTranslateX), maxTranslateX);
        translateY = Math.min(Math.max(translateY, -maxTranslateY), maxTranslateY);
        
        this.applyZoom(container, currentScale, translateX, translateY);
      }
    }, { passive: false });

    container.addEventListener('touchend', (e) => {
      if (e.touches.length < 2) {
        isZooming = false;
      }
      if (e.touches.length === 0) {
        isPanning = false;
      }
    });

    // Double tap to zoom
    container.addEventListener('touchend', (e) => {
      if (e.touches.length === 0 && !isZooming && !isPanning) {
        const currentTime = new Date().getTime();
        const tapLength = currentTime - lastTapTime;
        
        if (tapLength < 500 && tapLength > 0) {
          e.preventDefault();
          e.stopPropagation();
          
          if (currentScale > 1) {
            // Zoom out
            currentScale = 1;
            translateX = 0;
            translateY = 0;
            this.applyZoom(container, 1, 0, 0);
          } else {
            // Zoom in
            currentScale = 2;
            this.applyZoom(container, 2, translateX, translateY);
          }
        }
        lastTapTime = currentTime;
      }
    });

    // Handle single tap for modal opening (only when not zoomed)
    container.addEventListener('touchend', (e) => {
      if (e.touches.length === 0 && !isZooming && !isPanning && currentScale === 1) {
        const currentTime = new Date().getTime();
        const tapLength = currentTime - lastTapTime;
        
        // If it's a single tap (not double tap), trigger the modal
        if (tapLength > 500 || tapLength === 0) {
          // Find the slide index from the clickable container
          const slideIndex = this.getSlideIndex(clickableContainer);
          if (slideIndex) {
            // Dispatch the productmodal event
            window.dispatchEvent(new CustomEvent('productmodal', {
              detail: { current: slideIndex, open: true }
            }));
          }
        }
      }
    });
  },

  getSlideIndex(clickableContainer) {
    // Try to find the slide index from various sources
    const slide = clickableContainer.closest('.swiper-slide');
    if (slide) {
      const slideIndex = Array.from(slide.parentNode.children).indexOf(slide) + 1;
      return slideIndex;
    }
    
    // Fallback: try to find index from data attributes or text content
    const indexElement = clickableContainer.querySelector('[data-index]');
    if (indexElement) {
      return parseInt(indexElement.dataset.index);
    }
    
    return 1; // Default to first slide
  },

  getDistance(touch1, touch2) {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  },

  applyZoom(container, scale, translateX = 0, translateY = 0) {
    const transform = `scale(${scale}) translate(${translateX}px, ${translateY}px)`;
    container.style.transform = transform;
    container.style.transition = scale === 1 ? 'transform 0.3s ease-out' : 'none';
  },

  resetZoom() {
    const containers = this.$refs.slider.querySelectorAll('.zoom-container');
    containers.forEach(container => {
      this.applyZoom(container, 1, 0, 0);
    });
  },

  slideNext() {
    if (this.swiper) {
      this.swiper.slideTo(this.swiper.activeIndex + 1);
    }
  },
  
  slidePrev() {
    if (this.swiper) {
      this.swiper.slideTo(this.swiper.activeIndex - 1);
    }
  },

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
});
